// Machine tier picker, tier change, restart and usage graphs (dashboard and admin).

import { IconInfoCircle } from '@tabler/icons-react';
import { Tag } from '../ui/Tag';
import { Dialog } from '../ui/Dialog';
import { Empty, Segmented } from '../ui/ds';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import {
  machineState,
  machineTiers,
  machineUsage,
  rebootMachine,
  resizeMachine,
  restorePreview,
  type RestorePreview,
  type Tier,
  type TierChoices,
  type UsageRange,
  type UsageSample,
} from '../api';
import { escCloses } from '../ui/keys';
import { PixelMeter } from '../ui/PixelMeter';

// ---------- tier picker ----------

/** The tiers to pick from; machineId: an admin looking at someone's machine. */
export function useTierChoices(machineId?: string) {
  const [choices, setChoices] = useState<TierChoices | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    const r = await machineTiers(machineId);
    if (r.ok) {
      setChoices(r.data);
      setError(null);
    } else setError(r.error);
  }, [machineId]);
  useEffect(() => {
    void load();
  }, [load]);
  return { choices, error, reload: load };
}

export function tierName(choices: TierChoices | null, id: string | undefined) {
  return choices?.tiers.find((t) => t.id === id)?.name;
}

/** The sizes to choose from, as the Mac app's TierChoiceList: a card each, 8 apart — at the left the little
 *  machine stacked one, two or three high on the well (S, M, L; the current one's light on), then the name
 *  with the Current tag over its specs, the radio at the right. The picked one is ringed in ink. Each comes
 *  with its disk (a machine keeps a larger one it has). */
export function TierPicker({
  choices,
  tier,
  onTier,
  current,
  price,
}: {
  choices: { tiers: Omit<Tier, 'kind'>[] };
  tier: string;
  onTier: (id: string) => void;
  current?: string;
  /** the plan's price for a size, at the head's right (Change plan); Change size has none */
  price?: (id: string) => ReactNode;
}) {
  return (
    <div className="tier-picker" data-testid="tier-picker">
      <fieldset className="tier-list">
        <legend className="sr-only">Size</legend>
        {choices.tiers.map((t, i) => {
          const off = !t.available && t.id !== current;
          return (
            <label key={t.id} className={`tier-card${tier === t.id ? ' on' : ''}${off ? ' off' : ''}`} data-testid={`tier-${t.id}`}>
              <span className="tier-art" aria-hidden="true">
                {Array.from({ length: Math.min(i + 1, 3) }, (_, u) => (
                  <span key={u} className="tier-mini">
                    <span className="ds-mini" data-dim={t.id === current && u === 0 ? undefined : ''} />
                  </span>
                ))}
              </span>
              <span className="tier-text">
                <span className="tier-head">
                  <span className="tier-name">{t.name}</span>
                  {t.id === current && <Tag tone="accent">Current</Tag>}
                  {price && <span className="tier-price">{price(t.id)}</span>}
                </span>
                <span className="tier-specs">
                  {/* each figure whole: a line breaks between them, never inside one */}
                  <span>{t.vcpu} vCPU ·</span> <span>{t.memoryGB} GB memory ·</span> <span>{t.diskGB} GB disk</span>
                  {off && <> <span>· not available right now</span></>}
                </span>
              </span>
              <input className="tier-radio" type="radio" name="tier" value={t.id} checked={tier === t.id} disabled={off} onChange={() => onTier(t.id)} />
            </label>
          );
        })}
      </fieldset>
    </div>
  );
}

// ---------- change tier (after setup) ----------

/** machineId: an admin changing someone's machine (else the signed-in user's own, on a hub without plans). */
export function ChangeTier({ machineId, onChanged, dialog }: { machineId?: string; onChanged: () => void; dialog?: { onClose: () => void } }) {
  const [open, setOpen] = useState(!!dialog);
  const closePanel = () => (dialog ? dialog.onClose() : setOpen(false));
  const { choices, error: loadError, reload } = useTierChoices(machineId);
  const [tier, setTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const cur = choices?.current;
  // Its size, marked; a machine whose size isn't known has none picked (Apply waits for one).
  useEffect(() => {
    if (open && cur) setTier(cur.tier);
  }, [open, cur]);
  if (!open)
    return (
      <button className="btn" onClick={() => (setOpen(true), setMsg(null), void reload())} data-testid="change-tier">
        Change size
      </button>
    );
  const same = !!cur && tier === cur.tier;
  const shrinking = !!cur && !!choices && (choices.tiers.find((t) => t.id === tier)?.memoryGB ?? 0) < (choices.tiers.find((t) => t.id === cur.tier)?.memoryGB ?? 0);
  const panel = (
    <>
      {loadError && <div className="form-error">{loadError}</div>}
      {!choices && !loadError && <div className="skeleton" aria-busy="true" aria-label="Loading sizes" />}
      {choices && (
        <>
          <TierPicker choices={choices} tier={tier} current={cur?.tier} onTier={setTier} />
          {/* what happens, before Apply: nothing restarts (and, going smaller, where the rest goes); the Mac's info SettingsBanner */}
          <div className="tier-note" role="note" data-testid="change-tier-note">
            <IconInfoCircle size={14} stroke={1.5} aria-hidden="true" />
            <span>
              {shrinking
                ? 'Nothing restarts. Memory shrinks as programs let go of it, and your files stay put.'
                : 'Nothing restarts. The new size kicks in right away.'}
            </span>
          </div>
          {msg && <div className={msg.ok ? 'form-ok' : 'form-error'} role="status">{msg.text}</div>}
          <div className="form-actions">
            <button className="btn" onClick={closePanel}>
              Close
            </button>
            <button
              className="btn btn-primary"
              disabled={busy || same || !tier}
              data-testid="change-tier-apply"
              onClick={async () => {
                setBusy(true);
                setMsg(null);
                const r = await resizeMachine(tier, machineId);
                setBusy(false);
                if (!r.ok) return setMsg({ ok: false, text: r.error });
                setMsg({
                  ok: true,
                  text: r.data.applied?.restarted
                    ? 'Done. It restarts once to allow live changes from now on; your sessions reopen.'
                    : 'Done: the new size is live.',
                });
                void reload();
                onChanged();
              }}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </>
      )}
    </>
  );
  return dialog ? (
    <Dialog title="Change size" onClose={dialog.onClose}>
      <div data-testid="change-tier-panel">{panel}</div>
    </Dialog>
  ) : (
    <div className="panel" data-testid="change-tier-panel" onKeyDown={escCloses(() => setOpen(false))}>
      {panel}
    </div>
  );
}

// ---------- reboot ----------

/** asAdmin: an admin rebooting someone's machine (else the signed-in user's own). */
export function RebootControl({ machineId, asAdmin, onDone, dialog }: { machineId: string; asAdmin?: boolean; onDone?: () => void; dialog?: { onClose: () => void } }) {
  const [confirm, setConfirm] = useState(!!dialog);
  const [resume, setResume] = useState(true);
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const [phase, setPhase] = useState<'idle' | 'sending' | 'rebooting' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const since = useRef(0);
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (dialog) void restorePreview(asAdmin ? machineId : undefined).then((r) => r.ok && setPreview(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cancel = () => {
    setConfirm(false);
    setPhase('idle');
    setError(null);
    dialog?.onClose();
  };
  useEffect(() => {
    if (phase !== 'rebooting') return;
    setSlow(false);
    const late = setTimeout(() => setSlow(true), 180_000);
    const t = setInterval(async () => {
      // Give the power-off a moment before a "ready" can mean "back".
      if (Date.now() - since.current < 8000) return;
      const back = asAdmin ? Date.now() - since.current > 45000 : await machineState().then((r) => r.ok && r.data.machine.state === 'ready');
      if (back) {
        setPhase('idle');
        setConfirm(false);
        onDone?.();
        dialog?.onClose();
      }
    }, 2000);
    return () => (clearInterval(t), clearTimeout(late));
  }, [phase, onDone, asAdmin]);
  if (phase === 'rebooting') {
    const wait = (
      <span className="rebooting-wrap">
        <Tag tone="amber" data-testid="rebooting">
          <span className="dot dot-waiting" aria-hidden="true" /> Restarting…
        </Tag>
        {slow && <span className="muted small">Taking longer than usual; it keeps trying. If it isn’t back in a few minutes, tell us.</span>}
      </span>
    );
    return dialog ? <Dialog title={`Restarting ${machineId}`} onClose={dialog.onClose}>{wait}</Dialog> : wait;
  }
  if (!confirm)
    return (
      <button
        className="btn btn-ghost"
        onClick={() => {
          setConfirm(true);
          setResume(true);
          setPreview(null);
          void restorePreview(asAdmin ? machineId : undefined).then((r) => r.ok && setPreview(r.data));
        }}
        data-testid="reboot"
      >
        Restart…
      </button>
    );
  // the Mac's Restart dialog (MachineDialogs.swift): what it does, then the one choice as a settings row with a switch
  // (label + what comes back on the left), then Cancel and Restart (secondary, danger) at the right
  const confirmBody = (
    <>
      <p>Restarts {machineId}: every running program ends.</p>
      <label className="dlg-choice" data-testid="reboot-resume">
        <span className="dlg-choice-text">
          <strong>Continue where I left off</strong>
          <span>{restoreSummary(preview)}</span>
        </span>
        <input type="checkbox" role="switch" className="dlg-switch-input" checked={resume} disabled={phase === 'sending'} onChange={(e) => setResume(e.target.checked)} />
        <span className="ds-switch dlg-switch" aria-hidden="true" />
      </label>
      {error && <div className="form-error">{error}</div>}
      <div className="form-actions">
        <button className="btn" onClick={cancel}>
          Cancel
        </button>
        <button
          className="btn btn-danger"
          disabled={phase === 'sending'}
          data-testid="reboot-go"
          onClick={async () => {
            setPhase('sending');
            const r = await rebootMachine(resume, asAdmin ? machineId : undefined);
            if (r.ok) {
              since.current = Date.now();
              setPhase('rebooting');
            } else {
              setError(r.error);
              setPhase('error');
            }
          }}
        >
          {phase === 'sending' ? 'Restarting…' : 'Restart'}
        </button>
      </div>
    </>
  );
  return dialog ? (
    <Dialog title={`Restart ${machineId}?`} onClose={cancel}>
      <div data-testid="reboot-confirm">{confirmBody}</div>
    </Dialog>
  ) : (
    <div className="panel" role="alertdialog" aria-label={`Restart ${machineId}?`} data-testid="reboot-confirm" onKeyDown={escCloses(cancel)}>
      {confirmBody}
    </div>
  );
}

/** "Reopens your 6 terminal sessions and 3 desktop apps. …" */
function restoreSummary(p: RestorePreview | null) {
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  const parts: string[] = [];
  if (p) {
    if (p.sessions) parts.push(n(p.sessions, 'terminal session', 'terminal sessions'));
    if (p.apps) parts.push(n(p.apps, 'desktop app', 'desktop apps'));
    if (p.tabs) parts.push(n(p.tabs, 'Chrome tab', 'Chrome tabs'));
  }
  const what = parts.length ? `Reopens your ${parts.join(', ').replace(/, ([^,]*)$/, ' and $1')}. ` : 'Reopens your terminal sessions, desktop apps and Chrome tabs. ';
  const agents = p ? Object.keys(p.agents ?? {}) : [];
  return (
    what +
    (agents.length ? `${agents.join(', ')} ${agents.length === 1 ? 'resumes its conversation' : 'resume their conversations'}; ` : 'Coding agents resume their conversations; ') +
    'other commands are ready to re-run. Untick to start fresh.'
  );
}

// ---------- usage ----------

const RANGES: { id: UsageRange; label: string; span: number }[] = [
  { id: '1h', label: '1h', span: 3600 },
  { id: '1d', label: '1d', span: 86400 },
  { id: '7d', label: '7d', span: 7 * 86400 },
];

export function fmtBytes(n: number) {
  if (!Number.isFinite(n)) return '–';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}
export const fmtRate = (n: number) => `${fmtBytes(n)}/s`;

export function useUsage(range: UsageRange, machineId?: string) {
  const [samples, setSamples] = useState<UsageSample[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let last = 0;
    setSamples(null);
    const load = async () => {
      // The hour view grows by the new samples; the longer ones reload whole.
      const since = range === '1h' ? last : 0;
      const r = await machineUsage(range, since, machineId);
      if (!alive) return;
      if (!r.ok) return setError(r.error);
      setError(null);
      const got = r.data.samples ?? [];
      if (got.length) last = got[got.length - 1].t;
      setSamples((prev) => {
        const all = since && prev ? [...prev, ...got] : got;
        const cut = Date.now() / 1000 - RANGES.find((x) => x.id === range)!.span;
        return all.filter((s) => s.t >= cut);
      });
    };
    void load();
    const t = setInterval(() => document.visibilityState === 'visible' && void load(), range === '1h' ? 5000 : 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [range, machineId]);
  return { samples, error };
}

type Series = { values: number[]; label: string; tone: 'accent' | 'green' | 'amber' };

/** A small area/line chart with a hover readout. */
export function Chart({ times, series, max, fmt, span }: { times: number[]; series: Series[]; max?: number; fmt: (v: number) => string; span: number }) {
  const W = 300;
  const H = 56;
  const [hover, setHover] = useState<number | null>(null);
  const top = useMemo(() => Math.max(max ?? 0, ...series.flatMap((s) => s.values), 1e-9) * (max ? 1 : 1.15), [series, max]);
  const now = Date.now() / 1000;
  const x = (t: number) => ((t - (now - span)) / span) * W;
  const y = (v: number) => H - (v / top) * (H - 2) - 1;
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!times.length) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = now - span + ((e.clientX - r.left) / r.width) * span;
    let best = 0;
    for (let i = 1; i < times.length; i++) if (Math.abs(times[i] - t) < Math.abs(times[best] - t)) best = i;
    setHover(best);
  };
  return (
    <div className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" onPointerMove={onMove} onPointerLeave={() => setHover(null)} aria-hidden="true">
        {series.map((s, si) => {
          if (!s.values.length) return null;
          const pts = s.values.map((v, i) => `${x(times[i]).toFixed(1)},${y(v).toFixed(1)}`);
          return (
            <g key={si} className={`chart-${s.tone}`}>
              {si === 0 && <path className="chart-area" d={`M${x(times[0]).toFixed(1)},${H} L${pts.join(' L')} L${x(times[times.length - 1]).toFixed(1)},${H} Z`} />}
              <path className="chart-line" d={`M${pts.join(' L')}`} />
            </g>
          );
        })}
        {hover !== null && <line className="chart-hover" x1={x(times[hover])} x2={x(times[hover])} y1={0} y2={H} />}
      </svg>
      {hover !== null && (
        <div className="chart-readout">
          {new Date(times[hover] * 1000).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: span > 86400 ? 'numeric' : undefined, month: span > 86400 ? 'short' : undefined })}
          {series.map((s) => (
            <span key={s.label}>
              {' · '}
              {s.label} {fmt(s.values[hover])}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** live: its own Live line (off where the card around it already says it's live: the dashboard's Activity). */
export function UsagePanel({ machineId, compact, live = true }: { machineId?: string; compact?: boolean; live?: boolean }) {
  const [range, setRange] = useState<UsageRange>('1h');
  const { samples, error } = useUsage(range, machineId);
  const span = RANGES.find((r) => r.id === range)!.span;
  const s = samples ?? [];
  const times = s.map((x) => x.t);
  const last = s[s.length - 1];
  const pick = (f: (x: UsageSample) => number) => s.map(f);
  return (
    <div className={`usage${compact ? ' usage-compact' : ''}`} data-testid="usage">
      <div className="usage-head">
        <Segmented label="Time range" tabs={{}} value={range} onChange={setRange} items={RANGES.map((r) => ({ value: r.id, label: r.label, testid: `usage-${r.id}` }))} />
        {live && range === '1h' && (
          <span className="usage-live">
            <i aria-hidden="true" />
            Live · every 5 s
          </span>
        )}
      </div>
      {error && <p className="form-error">{error}</p>}
      {!samples && !error && <div className="skeleton" aria-busy="true" aria-label="Loading usage" />}
      {samples && !s.length && (
        <Empty size="md" art="machine" className="table-empty" data-testid="usage-empty" title="No usage yet" lead="CPU, memory and disk show up here a few seconds after the cloud machine starts." />
      )}
      {samples && s.length > 0 && last && (
        <div className="usage-rows">
          <div className="usage-row" data-testid="usage-cpu">
            <div className="usage-label">
              <span>CPU</span>
              <strong>{Math.round(last.cpu)}%</strong>
              <span className="muted small">of {last.cpus} vCPU</span>
            </div>
            <Chart times={times} span={span} max={100} fmt={(v) => `${Math.round(v)}%`} series={[{ values: pick((x) => x.cpu), label: 'CPU', tone: 'accent' }]} />
          </div>
          <div className="usage-row" data-testid="usage-memory">
            <div className="usage-label">
              <span>Memory</span>
              <strong>{fmtBytes(last.memUsed)}</strong>
              <span className="muted small">of {fmtBytes(last.memTotal)}</span>
            </div>
            <Chart times={times} span={span} max={Math.max(...pick((x) => x.memTotal))} fmt={fmtBytes} series={[{ values: pick((x) => x.memUsed), label: 'used', tone: 'accent' }]} />
          </div>
          {/* Swap in use means memory ran short; machines without swap don't show it. */}
          {!!last.swapTotal && (
            <div className="usage-row" data-testid="usage-swap">
              <div className="usage-label">
                <span>Swap</span>
                <strong>{fmtBytes(last.swapUsed ?? 0)}</strong>
                <span className="muted small">of {fmtBytes(last.swapTotal)}</span>
              </div>
              <Chart
                times={times}
                span={span}
                max={Math.max(...pick((x) => x.swapTotal ?? 0))}
                fmt={fmtBytes}
                series={[{ values: pick((x) => x.swapUsed ?? 0), label: 'swap', tone: 'amber' }]}
              />
            </div>
          )}
          <div className="usage-row" data-testid="usage-network">
            <div className="usage-label">
              <span>Network</span>
              <strong>↓ {fmtRate(last.netRx)}</strong>
              <span className="muted small">↑ {fmtRate(last.netTx)}</span>
            </div>
            <Chart
              times={times}
              span={span}
              fmt={fmtRate}
              series={[
                { values: pick((x) => x.netRx), label: 'in', tone: 'accent' },
                { values: pick((x) => x.netTx), label: 'out', tone: 'green' },
              ]}
            />
          </div>
          <div className="usage-row" data-testid="usage-disk">
            <div className="usage-label">
              <span>Disk</span>
              <strong>{fmtBytes(last.diskUsed)}</strong>
              <span className="muted small">of {fmtBytes(last.diskTotal)}</span>
            </div>
            <PixelMeter share={last.diskUsed / Math.max(1, last.diskTotal)} label="Disk used" />
          </div>
          <div className="usage-row" data-testid="usage-diskio">
            <div className="usage-label">
              <span>Disk I/O</span>
              <strong>R {fmtRate(last.diskRead)}</strong>
              <span className="muted small">W {fmtRate(last.diskWrite)}</span>
            </div>
            <Chart
              times={times}
              span={span}
              fmt={fmtRate}
              series={[
                { values: pick((x) => x.diskRead), label: 'read', tone: 'accent' },
                { values: pick((x) => x.diskWrite), label: 'write', tone: 'amber' },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
}
